import { useEffect, useState } from "react";
import { Tag } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { monthLabel } from "../lib/format";
import { Panel, Empty } from "./Widgets";
import { Checkbox } from "./ui/checkbox";
import { CompanyOptions } from "./Companies";

const BRANDS = ["Crumble", "Zappay"];
const TONE = { Crumble: "#EAB308", Zappay: "#06B6D4" };

export function BrandButtons({ value, onPick, testPrefix }) {
  return (
    <div className="flex gap-1">
      {BRANDS.map((b) => (
        <button key={b} data-testid={`${testPrefix}-${b}`} onClick={() => onPick(b)}
          className="text-[11px] font-mono uppercase px-2 py-1 rounded border transition-colors"
          style={value === b ? { background: `${TONE[b]}33`, borderColor: TONE[b], color: TONE[b] } : { borderColor: "#334155", color: "#94A3B8" }}>
          {b}
        </button>
      ))}
    </div>
  );
}

export function BrandBadge({ brand }) {
  if (!brand) return <span className="text-[10px] font-mono uppercase text-slate-600">untagged</span>;
  return <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border" style={{ borderColor: TONE[brand], color: TONE[brand] }}>{brand}</span>;
}

export function MerchantBrandPanel({ merchantId }) {
  const [brand, setBrand] = useState(null);
  useEffect(() => { api.get(`/merchants/${merchantId}/brand`).then((r) => setBrand(r.data.brand)).catch((e) => toast.error(errMsg(e))); }, [merchantId]);
  const pick = async (b) => {
    try { await api.put(`/merchants/${merchantId}/brand`, { brand: b }); setBrand(b); toast.success(`Marked as ${b} user`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title="Crumble or Zappay user" action={<Tag size={16} className="text-slate-400" />}>
      <div className="p-5 flex items-center gap-4" data-testid="merchant-brand-panel">
        <BrandBadge brand={brand} />
        {!brand && <span className="text-xs text-slate-500">Pick one:</span>}
        <BrandButtons value={brand} onPick={pick} testPrefix="merchant-brand" />
      </div>
    </Panel>
  );
}

export function PendingBrands({ month, className = "", full = false }) {
  const [d, setD] = useState(null);
  const [checked, setChecked] = useState([]);
  const [q, setQ] = useState("");
  const [co, setCo] = useState("");
  const load = () => api.get("/merchant-brands/pending", { params: month ? { month } : {} }).then((r) => setD(r.data))
    .catch((e) => { toast.error(errMsg(e)); setD({ merchants: [] }); });
  useEffect(() => { load(); setChecked([]); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps
  const apply = async (ids, brand) => {
    try {
      const { data } = await api.post("/merchant-brands/bulk", { merchant_ids: ids, brand });
      toast.success(`${data.updated} merchant${data.updated === 1 ? "" : "s"} marked ${brand}`); setChecked([]); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const needle = q.trim().toLowerCase();
  const list = (d?.merchants || []).filter((m) => (!co || m.company === co)
    && (!needle || `${m.merchant_name} ${m.merchant_id} ${m.owner}`.toLowerCase().includes(needle)));
  return (
    <Panel title={`${month ? `New merchants in ${monthLabel(month)}` : "Merchants not yet tagged"} · Crumble or Zappay${d ? ` · ${d.merchants.length}` : ""}`} className={className}>
      {full && d?.merchants.length > 0 && (
        <div className="flex flex-wrap gap-2 p-4 border-b border-slate-800" data-testid="untagged-filters">
          <input data-testid="untagged-search" value={q} onChange={(e) => { setQ(e.target.value); setChecked([]); }} placeholder="Search name, MID or agent"
            className="h-8 w-64 rounded-md bg-[#0B0F17] border border-slate-700 text-xs px-2" />
          <select data-testid="untagged-company" value={co} onChange={(e) => { setCo(e.target.value); setChecked([]); }} className="h-8 rounded-md bg-[#0B0F17] border border-slate-700 text-xs px-2">
            <CompanyOptions /></select>
          <span className="text-xs text-slate-500 self-center" data-testid="untagged-showing">Showing {list.length}</span>
        </div>)}
      {!d ? <Empty>Loading…</Empty> : d.merchants.length === 0 ? <Empty>{month ? "No new merchants to tag this month." : "Every merchant is tagged."}</Empty> : list.length === 0 ? <Empty>No untagged merchants match.</Empty> : (
        <div data-testid="pending-brands-panel">
          <div className="flex flex-wrap items-center gap-3 px-4 py-2 bg-[#0d1320] border-b border-slate-800">
            <Checkbox data-testid="pending-brands-select-all" checked={list.length > 0 && checked.length === list.length}
              onCheckedChange={(v) => setChecked(v ? list.map((m) => m.merchant_id) : [])} />
            <span className="text-xs text-slate-400 flex-1">{checked.length ? `${checked.length} selected — mark as:` : "Select merchants to tag many at once"}</span>
            {checked.length > 0 && <BrandButtons onPick={(b) => apply(checked, b)} testPrefix="pending-bulk-brand" />}
          </div>
          <div className={`${full ? "max-h-[640px]" : "max-h-[420px]"} overflow-y-auto divide-y divide-slate-800`}>
            {list.map((m) => (
              <div key={m.merchant_id} className="flex flex-wrap items-center gap-3 px-4 py-2" data-testid={`pending-brand-${m.merchant_id}`}>
                <Checkbox checked={checked.includes(m.merchant_id)} data-testid={`pending-brand-check-${m.merchant_id}`}
                  onCheckedChange={(v) => setChecked((c) => (v ? [...c, m.merchant_id] : c.filter((x) => x !== m.merchant_id)))} />
                <div className="flex-1 min-w-[180px]">
                  <div className="text-sm text-slate-100">{m.merchant_name || "—"}</div>
                  <div className="text-[11px] font-mono text-slate-500">{m.merchant_id} · {m.company} · {m.owner} · since {monthLabel(m.first_month)}</div>
                </div>
                <BrandButtons onPick={(b) => apply([m.merchant_id], b)} testPrefix={`pending-brand-${m.merchant_id}`} />
              </div>
            ))}
          </div>
        </div>
      )}
    </Panel>
  );
}
